<template>
  <main class="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-w-full">
    <el-form
      class="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full"
      :model="
        estimateForm.projectStages[phaseIndex].projectStageTasks[itemIndex]
      "
    >
      <el-form-item
        class="tw-col-span-4 md:tw-col-span-12"
        :class="{ 'is-error': !!subTaskErrors.name }"
      >
        <NoBgInput
          v-model="
            estimateForm.projectStages[phaseIndex].projectStageTasks[itemIndex]
              .name
          "
          placeholder="Material Description"
          @change="updateMaterialPhaseTask"
        />
      </el-form-item>
      <el-form-item
        v-if="!isDIYProjectType"
        :class="{ 'is-error': !!subTaskErrors.materialUnit }"
        class="tw-col-span-2 md:tw-col-span-6"
      >
        <div class="tw-flex tw-flex-row tw-w-full">
          <NoBgInput
            type="number"
            min="1"
            v-model.number="
              estimateForm.projectStages[phaseIndex].projectStageTasks[
                itemIndex
              ].materialUnit
            "
            @input="handleMaterialUnit"
            placeholder="Unit"
          />
        </div>
      </el-form-item>
      <el-form-item
        v-if="!isDIYProjectType"
        :class="{ 'is-error': !!subTaskErrors.taskCost }"
        class="tw-col-span-2 md:tw-col-span-6"
      >
        <div class="tw-flex tw-items-center tw-w-full tw-gap-2">
          <Text variant="p">£</Text>
          <NoBgInput
            @input="handleSubTotalTask($event)"
            @keypress="
              numChecker(
                $event,
                estimateForm.projectStages[phaseIndex].projectStageTasks[
                  itemIndex
                ].taskCost
              )
            "
            placeholder="Unit Cost"
            v-model="
              estimateForm.projectStages[phaseIndex].projectStageTasks[
                itemIndex
              ].taskCost
            "
            :formatter="
              (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            "
            :parser="(value) => value.replace(/^0+(?=\d)|\$|,/g, '')"
          />
        </div>
      </el-form-item>
      <div
        class="tw-flex tw-gap-3 tw-items-center tw-col-span-4 md:tw-col-span-12"
        v-if="!isDIYProjectType"
      >
        <Text
          variant="span"
          textWeight="500"
          textColor="rgba(12, 15, 74, 0.5)"
          whiteSpace="nowrap"
          >Total item cost:</Text
        >
        <LabelWithValue
          class=""
          :justifyStart="true"
          :data="formattedCurrency(totalItemCost)"
          label=""
        />
      </div>
    </el-form>
    <section class="tw-flex tw-items-center tw-justify-between tw-w-full">
      <Button
        class="!tw-w-auto tw-text-xs tw-font-semibold tw-text-[#0C0F4A] tw-tracking-[0.4px] button-custom"
        label="Delete Material"
        variant="button-custom"
        :isCapitalize="true"
        @click="onDeleteMaterialItem"
      >
        <template #prefix>
          <TrashIcon />
        </template>
      </Button>
    </section>
  </main>
</template>
<script setup>
import { computed, reactive, ref, onMounted } from "vue";
import { useStore } from "vuex";

import Button from "@/core/components/ui/general/Button.vue";
import NoBgInput from "@/core/components/ui/inputs/NoBgInput.vue";
import Text from "@/core/components/ui/general/Text.vue";
import TrashIcon from "@/core/components/icons/TrashIcon.vue";
import InfoIcon from "@/core/components/icons/InfoIcon.vue";
import LabelWithValue from "@/core/components/common/LabelWithValue.vue";
import { isNumber } from "@/core/helpers/number.helper";
import CurrencyFilter from "@/filters/currency.filter";
import { JOB_TEMPLATE_STORE } from "@/store/modules/job-template";
import { EstimatePhaseType } from "@/core/enums/estimateEnum";
import SelectMaterialAssignPhaseModal from "@/modules/jobs/components/modal/SelectMaterialAssignPhaseModal.vue";
import tradeEstimateService from "@/core/services/trade-estimate.service";

const store = useStore();
const emits = defineEmits(["on-update-material-task"]);
const durationOptions = [
  {
    value: 1,
    label: "Days",
  },
  {
    value: 2,
    label: "Weeks",
  },
];

const props = defineProps([
  "phaseIndex",
  "itemIndex",
  "item",
  "isDIYProjectType",
]);

const estimateForm = computed(
  () => store.getters[`${JOB_TEMPLATE_STORE}/estimateForm`]
);

const isFormReadOnly = computed(
  () => store.getters[`${JOB_TEMPLATE_STORE}/isFormReadOnly`]
);

const numChecker = (event, value) => {
  return isNumber(event, value);
};

const formattedCurrency = (value) => {
  return CurrencyFilter.formatToCurrency(value);
};

const onDeleteMaterialItem = async () => {
  if (!isFormReadOnly.value) {
    // Delete the task from estimateForm.projectStages
    const estimatePhaseTask = estimateForm.value.projectStages[
      props.phaseIndex
    ].projectStageTasks.find((data, idx) => idx === props.itemIndex);

    estimateForm.value.projectStages[props.phaseIndex].projectStageTasks =
      estimateForm.value.projectStages[
        props.phaseIndex
      ].projectStageTasks.filter((x, xi) => xi !== props.itemIndex);
    try {
      await store.dispatch(`${JOB_TEMPLATE_STORE}/removeEstimationPhaseTask`, {
        estimatePhaseTaskId: estimatePhaseTask.id,
      });
    } catch (error) {
      console.log();
    }
  }
};
const validCost = computed(() => {
  const MAX_LIMIT = 99999999;
  const cost =
    estimateForm.value.projectStages[props.phaseIndex].projectStageTasks[
      props.itemIndex
    ].taskCost;
  if (cost > MAX_LIMIT) {
    return true;
  }
  return false;
});
const handleSubTotalTask = (value) => {
  if (!value) {
    estimateForm.value.projectStages[props.phaseIndex].projectStageTasks[
      props.itemIndex
    ].taskCost = "0";
  } else {
    if (!validCost.value) updateMaterialPhaseTask();
  }
};
const handleMaterialUnit = (value) => {
  if (!value) {
    estimateForm.value.projectStages[props.phaseIndex].projectStageTasks[
      props.itemIndex
    ].materialUnit = "0";
  } else {
    updateMaterialPhaseTask();
  }
};
const totalItemCost = computed(() => {
  const phase = estimateForm.value.projectStages[props.phaseIndex];

  const totalCostPerUnit =
    phase.projectStageTasks[props.itemIndex].taskCost *
    phase.projectStageTasks[props.itemIndex].materialUnit;

  phase.projectStageTasks[props.itemIndex].total = totalCostPerUnit;

  let currentTotal = phase.projectStageTasks.reduce((sum, task, index) => {
    return sum + (parseFloat(task.total) || 0);
  }, 0);
  store.dispatch(`${JOB_TEMPLATE_STORE}/setEstimatePhaseSubTotal`, {
    phaseIndex: props.phaseIndex,
    newValue: currentTotal,
  });

  return totalCostPerUnit;
});
const updateMaterialPhaseTask = () => {
  const { name, materialUnit, taskCost } =
    estimateForm.value.projectStages[props.phaseIndex].projectStageTasks[
      props.itemIndex
    ];

  if (taskCost) {
    store.dispatch(`${JOB_TEMPLATE_STORE}/clearErrorField`, {
      phaseIndex: props.phaseIndex,
      subTaskIndex: props.itemIndex,

      field: "taskCost",
    });
  }
  if (materialUnit) {
    store.dispatch(`${JOB_TEMPLATE_STORE}/clearErrorField`, {
      phaseIndex: props.phaseIndex,
      subTaskIndex: props.itemIndex,
      field: "materialUnit",
    });
  }
  if (name) {
    store.dispatch(`${JOB_TEMPLATE_STORE}/clearErrorField`, {
      phaseIndex: props.phaseIndex,
      subTaskIndex: props.itemIndex,
      field: "name",
    });
  }
  emits("on-update-material-task");
};

const subTaskErrors = computed(() => {
  const errors =
    store.getters[`${JOB_TEMPLATE_STORE}/getProjectEstimateErrors`];
  if (props.itemIndex !== undefined) {
    return errors[props.phaseIndex]?.subErrors?.[props.itemIndex] || {};
  }
  return {};
});

const isShowAssignPhaseModal = ref(false);
const assignPhaseMaterial = ref([]);

onMounted(() => {
  if (estimateForm.value) {
    const isExistPhase =
      estimateForm.value.projectStages[props.phaseIndex].projectStageTasks[
        props.itemIndex
      ].assignedPhaseTo;

    if (isExistPhase) {
      assignPhaseMaterial.value = isExistPhase;
    } else assignPhaseMaterial.value = null;
  }
});
</script>
<style lang="scss" scoped>
.el-form-item {
  margin: 0;
}
:deep(.el-input__wrapper) {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
}
:deep(.el-input__prefix) {
  position: absolute;
  right: 0;
}
.is-error input,
.is-error select {
  border-color: red;
}
.button-custom {
  width: auto !important;
  color: rgba($blueDark, 1);
  @include fluidFont(12, 12, 1.2);
  background-color: transparent;
}
</style>
